"use client";

import { useEffect, useRef, useState } from "react";
import { ErrorNotice, Notice } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
} from "@/components/tool-kit/styles";
import { writeToClipboard } from "@/lib/clipboard";
import { t } from "@/lib/i18n/interpolate";
import {
  DATAMUSE_HOME_URL,
  type DatamuseFailure,
  definitionFor,
  type PartOfSpeech,
  type WordResult,
} from "@/lib/tool-kit/vocabulary/datamuse";
import type { LookupStatus } from "@/lib/tool-kit/vocabulary/use-datamuse";

/*
 * What the vocabulary widgets share: the Datamuse credit, the searching and error
 * states, a row of words you can click to look up, and a per-word copy button. Every
 * word on screen comes from the calling widget's strings, passed in as props.
 */

export interface LookupStrings {
  searching: string;
  errorNetwork: string;
  errorBusy: string;
  errorUnavailable: string;
  retry: string;
}

export interface CopyStrings {
  copy: string;
  copied: string;
  /** `Copy “{word}”` — the button's accessible name. */
  copyWord: string;
}

/** "Word data from the Datamuse API", linked — the acknowledgement Datamuse asks for. */
export function DatamuseCredit({ label }: { label: string }) {
  return (
    <p className={HINT}>
      <a
        className="underline underline-offset-4 hover:text-foreground"
        href={DATAMUSE_HOME_URL}
        rel="noopener noreferrer"
        target="_blank"
      >
        {label}
      </a>
    </p>
  );
}

function failureMessage(
  failure: DatamuseFailure | null,
  strings: LookupStrings
): string {
  if (failure === "rate-limited") {
    return strings.errorBusy;
  }
  if (failure === "unavailable" || failure === "timeout") {
    return strings.errorUnavailable;
  }
  return strings.errorNetwork;
}

/** The searching line and the error box with its retry button. Null when neither. */
export function LookupState({
  status,
  failure,
  strings,
  onRetry,
}: {
  status: LookupStatus;
  failure: DatamuseFailure | null;
  strings: LookupStrings;
  onRetry: () => void;
}) {
  if (status === "loading") {
    return (
      <output className="block text-muted-foreground text-sm">
        {strings.searching}
      </output>
    );
  }
  if (status === "error") {
    return (
      <div className="space-y-3">
        <ErrorNotice>{failureMessage(failure, strings)}</ErrorNotice>
        <button className={BUTTON_SECONDARY} onClick={onRetry} type="button">
          {strings.retry}
        </button>
      </div>
    );
  }
  return null;
}

/** The searched word's main sense: "show — verb: To display…". Null without one. */
export function MeaningCard({
  echo,
  posNames,
  meaningLine,
}: {
  echo: WordResult | null;
  posNames: Readonly<Record<PartOfSpeech, string>>;
  /** `{pos}: {definition}` */
  meaningLine: string;
}) {
  const definition = echo ? definitionFor(echo, echo.pos[0]) : null;
  if (!(echo && definition)) {
    return null;
  }
  return (
    <div className={CARD}>
      <p className="font-semibold text-foreground">{echo.word}</p>
      <p className="mt-1 text-muted-foreground text-sm leading-relaxed">
        {t(meaningLine, {
          pos: posNames[definition.pos],
          definition: definition.text,
        })}
      </p>
    </div>
  );
}

/** Copies one word; says so for two seconds. */
export function WordCopyButton({
  word,
  strings,
}: {
  word: string;
  strings: CopyStrings;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    },
    []
  );

  async function handleCopy() {
    if (!(await writeToClipboard(word))) {
      return;
    }
    setCopied(true);
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => setCopied(false), 2000);
  }

  return (
    <button
      aria-label={t(strings.copyWord, { word })}
      className={`${BUTTON_SMALL} shrink-0`}
      onClick={handleCopy}
      type="button"
    >
      {copied ? strings.copied : strings.copy}
    </button>
  );
}

export interface WordRow {
  word: string;
  /** A short marker after the word, such as "synonym". */
  badge?: string;
  /** A line under the word — usually its definition. */
  detail?: string;
}

/** Words with a detail line each; click a word to look it up. */
export function WordRows({
  rows,
  onSelect,
  copyStrings,
}: {
  rows: readonly WordRow[];
  onSelect: (word: string) => void;
  copyStrings: CopyStrings;
}) {
  return (
    <ul className="grid gap-2 sm:grid-cols-2">
      {rows.map((row) => (
        <li
          className="flex items-start gap-3 rounded-lg border border-border bg-card px-3 py-2"
          key={row.word}
        >
          <div className="min-w-0 flex-1">
            <p className="flex flex-wrap items-baseline gap-x-2">
              <button
                className="text-left font-medium text-primary underline-offset-4 hover:underline"
                onClick={() => onSelect(row.word)}
                type="button"
              >
                {row.word}
              </button>
              {row.badge ? (
                <span className="rounded bg-primary/10 px-1.5 py-0.5 text-primary text-xs">
                  {row.badge}
                </span>
              ) : null}
            </p>
            {row.detail ? (
              <p className="mt-0.5 line-clamp-2 text-muted-foreground text-xs leading-relaxed">
                {row.detail}
              </p>
            ) : null}
          </div>
          <WordCopyButton strings={copyStrings} word={row.word} />
        </li>
      ))}
    </ul>
  );
}

/** A compact cloud of words; click one to look it up. */
export function WordChips({
  words,
  onSelect,
  format,
}: {
  words: readonly string[];
  onSelect?: (word: string) => void;
  /** What a chip shows, when it is more than the word — "empirical evidence". */
  format?: (word: string) => string;
}) {
  return (
    <ul className="flex flex-wrap gap-2">
      {words.map((word) => (
        <li key={word}>
          {onSelect ? (
            <button
              className="rounded-full border border-border bg-card px-3 py-1 text-foreground text-sm transition-colors hover:border-primary hover:text-primary"
              onClick={() => onSelect(word)}
              type="button"
            >
              {format ? format(word) : word}
            </button>
          ) : (
            <span className="inline-block rounded-full border border-border bg-card px-3 py-1 text-foreground text-sm">
              {format ? format(word) : word}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** "Try: significant · show · argue" — example lookups for an empty widget. */
export function ExampleWords({
  label,
  words,
  onSelect,
}: {
  label: string;
  words: readonly string[];
  onSelect: (word: string) => void;
}) {
  return (
    <Notice>
      <span className="me-2">{label}</span>
      {words.map((word, index) => (
        <span key={word}>
          {index > 0 ? " · " : null}
          <button
            className="font-medium text-primary underline-offset-4 hover:underline"
            onClick={() => onSelect(word)}
            type="button"
          >
            {word}
          </button>
        </span>
      ))}
    </Notice>
  );
}

/** Bring a field back into view after a result click re-runs the search. */
export function revealField(id: string): void {
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: "smooth", block: "center" });
}
